import { Box, Flex, FormTextField, Text } from '@shared/components';
import { useFormContext, type FieldPath } from 'react-hook-form';

import { type PhysicalFeatureCheckConfig } from '../../containers';
import { type ExamLocationRequestRoomPhysicalInfoFormInputs } from '../../containers/exam-location-request-room-physical-info-form/schema';
import { FormBooleanRadio } from '../form-boolean-radio';
import FormImagesInput from '../form-images-input/form-images-input';

interface PhysicalInfoFeatureCheckRowProps {
  config: PhysicalFeatureCheckConfig;
  isReadonly?: boolean;
  uploadFiles?: (files: File[]) => Promise<string[]>;
  getFilesDownloadSignedUrls?: (fileNames: string[]) => Promise<string[]>;
}

export default function PhysicalInfoFeatureCheckRow(props: PhysicalInfoFeatureCheckRowProps) {
  const { config, isReadonly = false, uploadFiles, getFilesDownloadSignedUrls } = props;
  const { key, label, required, hasImages, imageInputLabel, helperText } = config;
  const validFieldName = `${key}.valid` as FieldPath<ExamLocationRequestRoomPhysicalInfoFormInputs>;
  const additionalInfoFieldName = `${key}.additionalInfo` as const;
  const imagesFieldName = `${key}.images` as const;

  const { watch } = useFormContext<ExamLocationRequestRoomPhysicalInfoFormInputs>();
  const isValidSelected = typeof watch(validFieldName) === 'boolean';

  return (
    <Flex
      sx={(theme) => ({
        flexDirection: 'row',
        gap: theme.spacing(6),
        justifyContent: 'space-between',
        [theme.breakpoints.down('desktop')]: {
          flexDirection: 'column',
          gap: theme.spacing(2),
        },
      })}
    >
      <Box sx={{ width: '320px' }}>
        <Text variant='body1Semibold' sx={{ whiteSpace: 'nowrap' }}>
          {label}
          {required ? (
            <Text
              component='span'
              sx={(theme) => ({ marginLeft: theme.spacing(1) })}
              color='error.main'
            >
              *
            </Text>
          ) : null}
        </Text>
      </Box>
      <Flex
        sx={(theme) => ({
          flex: 1,
          flexDirection: 'column',
          gap: theme.spacing(2),
        })}
      >
        <FormBooleanRadio name={validFieldName} disabled={isReadonly} />
        <FormTextField
          name={additionalInfoFieldName}
          placeholder='กรอกรายละเอียดเพิ่มเติม'
          fullWidth
          disabled={isReadonly || !isValidSelected}
          helperText={helperText}
        />
        {hasImages ? (
          <FormImagesInput
            name={imagesFieldName}
            label={imageInputLabel}
            disabled={isReadonly}
            uploadFiles={uploadFiles}
            getDownloadSignedUrls={getFilesDownloadSignedUrls}
          />
        ) : null}
      </Flex>
    </Flex>
  );
}
